import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { useToast } from '../../components/ds';
import { Button, Table, Badge, Spinner } from '../../components/ds';
import PlacementTypeModal from './PlacementTypeModal';
import FieldConfigModal from './FieldConfigModal';
import './TemplatesPage.css';

export default function TemplatesPage() {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [placementTypes, setPlacementTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [fieldConfigTemplate, setFieldConfigTemplate] = useState(null);
  const { addToast } = useToast();

  const fetchData = useCallback(async () => {
    try {
      const [tpls, types] = await Promise.all([
        api.getTemplates(),
        api.getPlacementTypes(),
      ]);
      setTemplates(tpls || []);
      setPlacementTypes(types || []);
    } catch (err) {
      addToast('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePlacementChange = async (template, typeId) => {
    try {
      await api.updateTemplate(template.uuid, {
        template_name: template.name,
        placement_type_id: typeId || null,
      });
      addToast(typeId ? 'Template enabled' : 'Template disabled', 'success');
      fetchData();
    } catch (err) {
      addToast('Failed to update: ' + err.message);
    }
  };

  const handleDeleteType = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Templates using it will become unavailable.`)) return;
    try {
      await api.deletePlacementType(id);
      addToast('Deleted', 'success');
      fetchData();
    } catch (err) {
      addToast('Failed to delete: ' + err.message);
    }
  };

  const handleTypeModalSave = () => {
    setShowTypeModal(false);
    setEditingType(null);
    fetchData();
  };

  if (loading) {
    return <div className="loading"><Spinner /></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Template 管理</h1>
      </div>

      {/* Tabs - 暫時保留自建，Phase 2 會提供 <Tabs> 元件 */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Template 清單
        </button>
        <button
          className={`tab ${activeTab === 'types' ? 'active' : ''}`}
          onClick={() => setActiveTab('types')}
        >
          Placement Type 管理
        </button>
      </div>

      {activeTab === 'templates' && (
        <Table
          columns={[
            { key: 'name', title: 'Template 名稱' },
            { key: 'status', title: '狀態', render: (val) => (
              <Badge variant={val === 'active' ? 'primary' : 'default'}>{val}</Badge>
            )},
            { key: 'placement_type_id', title: 'Placement Type', render: (val, row) => (
              <select
                value={val || ''}
                onChange={(e) => handlePlacementChange(row, e.target.value ? parseInt(e.target.value) : null)}
                style={{ padding: '4px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}
              >
                <option value="">不可用</option>
                {placementTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.display_name || pt.name}
                  </option>
                ))}
              </select>
            )},
            { key: 'actions', title: '欄位設定', render: (_, row) => (
              <Button
                variant="secondary"
                size="sm"
                disabled={!row.placement_type_id}
                title={row.placement_type_id ? '設定欄位顯示/分類' : '請先選擇 Placement Type'}
                onClick={() => row.placement_type_id && setFieldConfigTemplate(row)}
              >
                設定
              </Button>
            )},
          ]}
          data={templates}
          rowKey="uuid"
          emptyText="No templates found"
        />
      )}

      {activeTab === 'types' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Button onClick={() => setShowTypeModal(true)}>+ 新增 Placement Type</Button>
          </div>
          <Table
            columns={[
              { key: 'name', title: '名稱', render: (val) => <code>{val}</code> },
              { key: 'display_name', title: '顯示名稱', render: (val) => val || '-' },
              { key: 'platform', title: '平台', render: (val) => val || '-' },
              { key: 'template_count', title: 'Template 數量' },
              { key: 'actions', title: '操作', render: (_, row) => (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="secondary" size="sm" onClick={() => { setEditingType(row); setShowTypeModal(true); }}>
                    編輯
                  </Button>
                  <Button variant="secondary" size="sm" style={{ color: 'var(--status-error-text)' }} onClick={() => handleDeleteType(row.id, row.name)}>
                    刪除
                  </Button>
                </div>
              )},
            ]}
            data={placementTypes}
            rowKey="id"
            emptyText="No placement types"
          />
        </div>
      )}

      {fieldConfigTemplate && (
        <FieldConfigModal
          template={fieldConfigTemplate}
          onClose={() => setFieldConfigTemplate(null)}
        />
      )}

      {showTypeModal && (
        <PlacementTypeModal
          type={editingType}
          onClose={() => { setShowTypeModal(false); setEditingType(null); }}
          onSaved={handleTypeModalSave}
        />
      )}
    </div>
  );
}
